import { Upsell } from '@/@types/Upsell';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Link2,
  TrendingDown,
  TrendingUp,
  Unplug,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ConnectionTarget = { type: 'upsell'; upsellPage: string };

interface DisconnectedUpsellsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  disconnectedUpsells: Upsell[];
  connectedUpsells: Upsell[];
  onConnect: (
    sourceUpsellId: string,
    direction: 'accept' | 'decline',
    target: ConnectionTarget,
  ) => Promise<void>;
}

export function DisconnectedUpsellsDialog({
  isOpen,
  onClose,
  disconnectedUpsells,
  connectedUpsells,
  onConnect,
}: DisconnectedUpsellsDialogProps) {
  const { t } = useTranslation('funnel');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [parentsMap, setParentsMap] = useState<Record<string, string>>({});
  const [directionsMap, setDirectionsMap] = useState<Record<string, 'accept' | 'decline'>>({});
  const [loadingSet, setLoadingSet] = useState<Set<string>>(new Set());
  const [successSet, setSuccessSet] = useState<Set<string>>(new Set());

  const getDirection = (id: string) => directionsMap[id] ?? 'accept';
  const getParent = (id: string) => parentsMap[id] ?? '';

  useEffect(() => {
    if (!isOpen) {
      setExpandedId(null);
      setParentsMap({});
      setDirectionsMap({});
      setLoadingSet(new Set());
      setSuccessSet(new Set());
    }
  }, [isOpen]);

  const handleConnect = async (u: Upsell) => {
    const id = String(u.id);
    const parent = getParent(id);
    if (!parent) return;

    setLoadingSet(prev => new Set(Array.from(prev).concat(id)));
    try {
      await onConnect(parent, getDirection(id), { type: 'upsell', upsellPage: u.page });
      setSuccessSet(prev => new Set(Array.from(prev).concat(id)));
      setExpandedId(null);
    } finally {
      setLoadingSet(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const pending = disconnectedUpsells.filter(u => !successSet.has(String(u.id)));

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent
        className={[
          'bg-[#18181B] border-[#27272a] text-white p-0 overflow-hidden gap-0',
          'sm:max-w-lg',
          'max-sm:!fixed max-sm:!inset-x-0 max-sm:!bottom-0 max-sm:!top-auto',
          'max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!left-0',
          'max-sm:!max-w-none max-sm:!w-full max-sm:!rounded-b-none max-sm:!rounded-t-2xl',
          'max-sm:!max-h-[90dvh]',
        ].join(' ')}
      >
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-zinc-600" />
        </div>

        <DialogHeader className="px-5 sm:px-6 pt-4 sm:pt-6 pb-4 border-b border-[#27272a]">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15 ring-1 ring-amber-500/30 flex-shrink-0">
              <Unplug className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <DialogTitle className="text-white text-base">
                {t('disconnected_title')}
              </DialogTitle>
              <DialogDescription className="text-zinc-400 text-xs mt-0.5">
                {pending.length === 0
                  ? t('disconnected_all_connected')
                  : pending.length === 1
                    ? t('disconnected_count', { count: pending.length })
                    : t('disconnected_count_plural', { count: pending.length })}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="p-4 sm:p-4 space-y-2.5 max-h-[60vh] max-sm:max-h-[calc(90dvh-140px)] overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom,16px)]">
          {disconnectedUpsells.length === 0 && (
            <div className="text-center py-10">
              <CheckCircle2 className="h-12 w-12 text-emerald-500/30 mx-auto mb-3" />
              <p className="text-sm font-medium text-white">{t('disconnected_all_connected')}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {t('disconnected_all_connected_desc', 'Todos os upsells estão conectados ao funil')}
              </p>
            </div>
          )}

          {disconnectedUpsells.map(u => {
            const id = String(u.id);
            const isUp = u.funnel_type === 'up_sell';
            const TypeIcon = isUp ? TrendingUp : TrendingDown;
            const isExpanded = expandedId === id;
            const isLoading = loadingSet.has(id);
            const isSuccess = successSet.has(id);

            return (
              <div
                key={u.id}
                className={`rounded-xl border overflow-hidden transition-all duration-200 ${isSuccess
                  ? 'border-emerald-500/30 bg-emerald-500/5'
                  : 'border-[#27272a] bg-[#09090b] hover:border-[#3f3f46]'
                  }`}
              >
                <div className="flex items-center gap-3 p-3.5 max-sm:flex-col max-sm:items-stretch max-sm:gap-2.5 max-sm:p-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`flex h-9 w-9 max-sm:h-10 max-sm:w-10 items-center justify-center rounded-lg flex-shrink-0 ${isSuccess
                      ? 'bg-emerald-500/15 ring-1 ring-emerald-500/30'
                      : isUp
                        ? 'bg-emerald-500/15 ring-1 ring-emerald-500/30'
                        : 'bg-rose-500/15 ring-1 ring-rose-500/30'
                      }`}>
                      {isSuccess
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        : <TypeIcon className={`h-4 w-4 ${isUp ? 'text-emerald-400' : 'text-rose-400'}`} />
                      }
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-white truncate">{u.name}</p>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${isUp ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'
                          }`}>
                          {isUp ? t('upsell') : t('downsell')}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 truncate mt-0.5">
                        {u.offer?.name} • {u.offer?.price_formatted}
                      </p>
                    </div>
                  </div>

                  {/* Action button — full width on mobile for larger touch target */}
                  {isSuccess ? (
                    <span className="flex items-center justify-center gap-1.5 text-[11px] font-medium text-emerald-400 flex-shrink-0 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1 max-sm:py-2.5 max-sm:rounded-lg">
                      <CheckCircle2 className="h-3 w-3" />
                      {t('connected', 'Conectado')}
                    </span>
                  ) : (
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : id)}
                      className="flex items-center justify-center gap-1.5 rounded-lg bg-primary/10 border border-primary/20 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/20 active:bg-primary/25 transition-colors flex-shrink-0 min-h-[44px] max-sm:w-full max-sm:py-3"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      <span>{t('disconnected_connect_to_funnel', 'Conectar')}</span>
                      {isExpanded
                        ? <ChevronDown className="h-3 w-3" />
                        : <ChevronRight className="h-3 w-3" />
                      }
                    </button>
                  )}
                </div>

                <AnimatePresence>
                  {isExpanded && !isSuccess && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      <div className="px-3.5 pb-3.5 pt-3 max-sm:px-4 max-sm:pb-4 max-sm:pt-4 border-t border-[#27272a] space-y-3 max-sm:space-y-4">
                        <div>
                          <p className="text-[11px] max-sm:text-xs font-medium text-zinc-400 mb-2">
                            {t('disconnected_connect_as')}
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setDirectionsMap(p => ({ ...p, [id]: 'accept' }))}
                              className={`flex-1 py-2 max-sm:py-3 rounded-lg text-xs max-sm:text-sm font-medium border transition-colors min-h-[44px] ${getDirection(id) === 'accept'
                                ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                                : 'bg-[#18181B] border-[#27272a] text-zinc-400 hover:border-zinc-600'
                                }`}
                            >
                              {t('accept_label')} ✓
                            </button>
                            <button
                              onClick={() => setDirectionsMap(p => ({ ...p, [id]: 'decline' }))}
                              className={`flex-1 py-2 max-sm:py-3 rounded-lg text-xs max-sm:text-sm font-medium border transition-colors min-h-[44px] ${getDirection(id) === 'decline'
                                ? 'bg-red-500/15 border-red-500/30 text-red-400'
                                : 'bg-[#18181B] border-[#27272a] text-zinc-400 hover:border-zinc-600'
                                }`}
                            >
                              {t('decline_label')} ✗
                            </button>
                          </div>
                        </div>

                        <div>
                          <p className="text-[11px] max-sm:text-xs font-medium text-zinc-400 mb-2">
                            {t('disconnected_from_which')}
                          </p>
                          {connectedUpsells.length === 0 ? (
                            <p className="text-[11px] max-sm:text-xs text-zinc-500 italic">
                              {t('disconnected_no_connected', 'Nenhum upsell conectado encontrado. Configure o funil primeiro.')}
                            </p>
                          ) : (
                            <Select
                              value={getParent(id)}
                              onValueChange={val => setParentsMap(p => ({ ...p, [id]: val }))}
                            >
                              <SelectTrigger className="w-full bg-[#09090B] border-[#27272a] min-h-[44px]">
                                <SelectValue placeholder={t('disconnected_select')} />
                              </SelectTrigger>
                              <SelectContent>
                                {connectedUpsells.map(cu => (
                                  <SelectItem key={cu.id} value={String(cu.id)}>
                                    {cu.name} ({cu.funnel_type === 'up_sell' ? t('upsell') : t('downsell')})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>

                        <Button
                          size="sm"
                          onClick={() => handleConnect(u)}
                          disabled={!getParent(id) || isLoading || connectedUpsells.length === 0}
                          className="w-full min-h-[44px] max-sm:text-sm"
                        >
                          {isLoading
                            ? t('disconnected_connecting')
                            : t('disconnected_connect_to_funnel')}
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

